import { Component, OnInit, OnDestroy, Directive, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { MiscTools } from 'appshared/misc-tools';
import { TextTools } from 'appshared/text-tools';
import { UITools } from 'appshared/ui-tools';
import { ValidationTools } from 'appshared/validation-tools';

import TrackRecent from '../../../../helpers/track-recent';

import { ZenCustomersService } from '../zen-customers.service';
import { OrganizationsService } from '../../organizations/organizations.service';
import { UsersService } from '../../users/users.service';
import { AuthService } from 'client/app/services/auth.service';
import { ProductsService } from '../../products/products.service';

// import { PagingSearchBarComponent } from 'client/app/components/shared/paging-search-bar/paging-search-bar.component';
import { TableZensComponent } from 'client/app/components/shared/table-zens/table-zens.component';

@Component({
	selector: 'app-zen-customers-list',
	templateUrl: './zen-customers-list.component.html',
	styleUrls: ['./zen-customers-list.component.scss']
})
export class ZenCustomersListComponent implements OnInit {
	ac = AppConstants;
	tt = TextTools;
	mt = MiscTools;
	ut = UITools;

	// @ViewChild(PagingSearchBarComponent) pagingSearchBar: PagingSearchBarComponent = null;
	@ViewChild('zensTable1') zensTable1: TableZensComponent = null;

	private userSubscription: Subscription;
	authUser: Models.AuthUser;

	zens: Models.ZenMasterCustomer[] = [];
	zensToShow: Models.ZenMasterCustomer[] = [];

	loading = true;
	organizations: Models.Organization[] = [];

	canAdd = false;

	// others
	showFilters = false;
	theForm: UntypedFormGroup;

	// things to choose from
	enabledChoices = [];
	assessmentChoices = [];
	zmTypeChoices = [];
	recentTrafficChoices = [];
	activeBxChoices = [];

	accountOwnerChoices: any[] = [];
	seChoices: any[] = [];

	specialChoices: any[] = [
		{ value: 'account_owner', label: 'I am the account owner' },
		{ value: 'tech_rep', label: 'I am a tech rep' },
		{ value: 'not_active', label: 'Site appears to be inactive' },
		{ value: 'no_notifications', label: 'Not setup for portal notifications' },
	];

	// filters
	enabledFilter: number = null;
	assessmentFilters = [];
	zmTypeFilters = [];
	recentTrafficFilters = [];
	activeBxFilters = [];
	accountOwnerFilters: any[] = [];
	seFilters: any[] = [];
	specialFilters: any[] = [];

	cachingUsageInfo: string = '';

	// extraInfo: any = {};

	recentZenCustomers: Models.ZenMasterCustomer[] = [];

	// new table stuff
	storageKey: string = 'cp-zenmasters-list';

	constructor(
		private router: Router,
		private authService: AuthService,
		private zenCustomersService: ZenCustomersService,
		private usersService: UsersService,
		private productsService: ProductsService,
		private organizationsService: OrganizationsService) {
	}

	// ------------------------------------------------------------------------
	ngOnInit(): void {
		this.userSubscription = this.authService.user.subscribe(authUser => {
			this.authUser = authUser;

			let settings: any = {};
			try {
				if (localStorage.getItem('cp-zenCustomers.settings'))
					settings = JSON.parse(localStorage.getItem('cp-zenCustomers.settings'));
			} catch (e: any) { }
			const settingKeys: string[] = Object.keys(settings);

			if (settingKeys.includes('enabledFilter')) this.enabledFilter = settings.enabledFilter;
			if (settingKeys.includes('assessmentFilters')) this.assessmentFilters = settings.assessmentFilters;
			if (settingKeys.includes('zmTypeFilters')) this.zmTypeFilters = settings.zmTypeFilters;
			if (settingKeys.includes('recentTrafficFilters')) this.recentTrafficFilters = settings.recentTrafficFilters;
			if (settingKeys.includes('activeBxFilters')) this.activeBxFilters = settings.activeBxFilters;
			if (settingKeys.includes('accountOwnerFilters')) this.accountOwnerFilters = settings.accountOwnerFilters;
			if (settingKeys.includes('seFilters')) this.seFilters = settings.seFilters;
			if (settingKeys.includes('specialFilters')) this.specialFilters = settings.specialFilters;

			this.showFilters = (
				this.enabledFilter != null
				|| this.assessmentFilters.length !== 0
				|| this.zmTypeFilters.length !== 0
				|| this.recentTrafficFilters.length !== 0
				|| this.activeBxFilters.length !== 0
				|| this.accountOwnerFilters.length !== 0
				|| this.seFilters.length !== 0
				|| this.specialFilters.length !== 0
			);

			this.loadData();
		});
	}

	// ------------------------------------------------------------------------
	async loadData(forceReload = false) {

		this.loading = true;

		if (forceReload) {
			this.zenCustomersService.refreshAll();
			this.organizationsService.refreshAll();
			await MiscTools.delay(3000);
		} // if

		this.zens = this.zenCustomersService.getAll();
		this.organizations = this.organizationsService.getAll();

		const recentIds = TrackRecent.getRecent('zenmaster');
		recentIds.reverse();

		this.recentZenCustomers = [];
		for (const recentId of recentIds) {
			const zm = this.zenCustomersService.getOne(recentId);
			if (zm) this.recentZenCustomers.push(zm);
		} // for

		this.canAdd = ValidationTools.checkAccess(this.authUser, 'admin-zen-master');

		const allUsers = this.usersService.getAll();
		const allProducts = this.productsService.getAll();
		let zenID = -1;
		for (const prod of allProducts)
			if (prod.name === 'ZEN Master')
				zenID = prod.id;

		let zenOrgIDs: number[] = [];
		if (zenID !== -1) {
			const allOrgs = this.organizationsService.getAll();
			for (const org of allOrgs)
				if (MiscTools.findIndexGeneric(org.product_platforms, 'product_id', zenID) !== -1)
					zenOrgIDs.push(org.id);
		} // if

		const zenUsers = await this.zenCustomersService.getZenUsers();

		// this.extraInfo = {};
		let usageCached: boolean = false;
		for (const item of this.zens) {
			let orgName = '';
			let accountOwnerName = '';
			let accountSEName = '';
			let accountTamName = '';
			let accountSAName = '';

			if (item.zcp_org_id && item.zcp_org_id !== 0) {
				const org = this.organizationsService.getOne(item.zcp_org_id);
				if (org) {
					orgName = org.name;
					accountOwnerName = org.salesforce_account_owner;
					accountSEName = org.salesforce_se;
					accountTamName = org.salesforce_tam;
					accountSAName = org.salesforce_sa;
				} // if
			} // if

			let subscribedCount = 0;
			for (const user of zenUsers) {
				if (user.customer_id === item.id) {
					const notifyInfo = ValidationTools.getZenUserNotificationState(allUsers, zenOrgIDs, user);
					if (notifyInfo === 'Subscribed') subscribedCount++;
				} // if
			} // for

			item['__notifyWarning'] = (subscribedCount === 0);
			item['__orgName'] = orgName;
			item['__accountOwner'] = accountOwnerName;
			item['__accountOwnerShort'] = TextTools.acronym(accountOwnerName);
			item['__se'] = accountSEName;
			item['__seShort'] = TextTools.acronym(accountSEName);
			item['__tam'] = accountTamName;
			item['__tamShort'] = TextTools.acronym(accountTamName);
			item['__sa'] = accountSAName;
			item['__saShort'] = TextTools.acronym(accountSAName);

			// this.extraInfo[item.id] = {
			// 	orgName,
			// 	accountOwnerName,
			// 	shortAccountOwnerName: TextTools.acronym(accountOwnerName),
			// 	accountSEName,
			// 	shortAccountSEName: TextTools.acronym(accountSEName),
			// 	notifyWarning
			// };

			item['__zenUsage'] = this.getUsage(item, 'zenmaster');
			item['__mcUsage'] = this.getUsage(item, 'mediaconnect');

			if (isNaN(item['__zenUsage'])) item['__zenUsage'] = 0;
			if (isNaN(item['__mcUsage'])) item['__mcUsage'] = 0;

			if (+item['__zenUsage'] !== 0 || +item['__mcUsage'] !== 0) usageCached = true;
		} // for

		if (!usageCached && this.zens.length > 0) {
			this.cachingUsageInfo = 'Caching Usage Info';

			await this.zenCustomersService.updateKeyUsage();

			for (const item of this.zens) {
				item['__zenUsage'] = await this.getUsage(item, 'zenmaster');
				item['__mcUsage'] = await this.getUsage(item, 'mediaconnect');
			} // for

			// this.setSortBy(null);
			this.cachingUsageInfo = '';
		} // if

		this.zmTypeChoices = [];
		this.zmTypeChoices.push({ value: '', label: 'Unknown/Not Set' });
		for (const zenMasterType of AppConstants.zenMasterTypes)
			this.zmTypeChoices.push({ value: zenMasterType, label: AppConstants.zenMasterTypeLabels[zenMasterType] });

		this.assessmentChoices = [];
		for (const zenMasterAssessment of AppConstants.zenMasterAssessments)
			this.assessmentChoices.push({ value: zenMasterAssessment });

		this.enabledChoices = [];
		this.enabledChoices.push({ value: 1, label: 'Enabled/Online' });
		this.enabledChoices.push({ value: 0, label: 'Disabled/Offline' });

		this.recentTrafficChoices = [];
		this.recentTrafficChoices.push({ value: 1, label: 'Has Traffic in Last ' + AppConstants.assessmentThresholdDays + ' Days' });
		this.recentTrafficChoices.push({ value: 0, label: 'No Traffic in Last ' + AppConstants.assessmentThresholdDays + ' Days' });

		this.activeBxChoices = [];
		this.activeBxChoices.push({ value: 1, label: 'Has at Least One Active Bx' });
		this.activeBxChoices.push({ value: 0, label: 'No Active Bx' });

		this.accountOwnerChoices = [];
		this.seChoices = [];
		for (const org of this.organizations) {
			if (org.salesforce_account_owner_id && org.salesforce_account_owner_id !== 0) {
				const idx1 = MiscTools.findIndexGeneric(this.accountOwnerChoices, 'value', org.salesforce_account_owner);
				if (idx1 === -1)
					this.accountOwnerChoices.push({ value: org.salesforce_account_owner, label: org.salesforce_account_owner });
			} // if

			if (org.salesforce_se_id && org.salesforce_se_id !== 0) {
				const idx1 = MiscTools.findIndexGeneric(this.seChoices, 'value', org.salesforce_se);
				if (idx1 === -1)
					this.seChoices.push({ value: org.salesforce_se, label: org.salesforce_se });
			} // if
		} // for

		this.accountOwnerChoices.sort((a, b) => (a.label > b.label) ? 1 : -1);
		this.seChoices.sort((a, b) => (a.label > b.label) ? 1 : -1);

		this.theForm = new UntypedFormGroup({
			enabledFilter: new UntypedFormControl(this.enabledFilter),
			assessmentFilters: new UntypedFormControl(this.assessmentFilters),
			zmTypeFilters: new UntypedFormControl(this.zmTypeFilters),
			recentTrafficFilters: new UntypedFormControl(this.recentTrafficFilters),
			activeBxFilters: new UntypedFormControl(this.activeBxFilters),
			accountOwnerFilters: new UntypedFormControl(this.accountOwnerFilters),
			seFilters: new UntypedFormControl(this.seFilters),
			specialFilters: new UntypedFormControl(this.specialFilters)
		});

		this.loading = false;

		await this.filterZens();

		// cache the stats blocks...
		for (const mode of AppConstants.zenMasterReportModes)
			await this.zenCustomersService.getZenCustomerInfo(mode, forceReload);
	}

	// ------------------------------------------------------------------------
	async getUsage(zc: Models.ZenMasterCustomer, type: string) {
		return this.zenCustomersService.getUsageNoLoad(zc, type);
	}

	// ------------------------------------------------------------------------
	async onFormChange() {
		// console.log(this.theForm.value.objTypes);
		// console.log(this.theForm.value);

		this.enabledFilter = this.theForm.value.enabledFilter;
		this.assessmentFilters = this.theForm.value.assessmentFilters;
		this.zmTypeFilters = this.theForm.value.zmTypeFilters;
		this.recentTrafficFilters = this.theForm.value.recentTrafficFilters;
		this.activeBxFilters = this.theForm.value.activeBxFilters;
		this.accountOwnerFilters = this.theForm.value.accountOwnerFilters;
		this.seFilters = this.theForm.value.seFilters;
		this.specialFilters = this.theForm.value.specialFilters;

		let settings: any = {
			enabledFilter: this.enabledFilter,
			assessmentFilters: this.assessmentFilters,
			zmTypeFilters: this.zmTypeFilters,
			recentTrafficFilters: this.recentTrafficFilters,
			activeBxFilters: this.activeBxFilters,
			accountOwnerFilters: this.accountOwnerFilters,
			seFilters: this.seFilters,
			specialFilters: this.specialFilters
		};
		localStorage.setItem('cp-zenCustomers.settings', JSON.stringify(settings));
		localStorage.setItem('cp-orgsList.settings', JSON.stringify(settings));

		this.filterZens();
	}

	// ------------------------------------------------------------------------
	toggleFilters() {
		this.showFilters = !this.showFilters;
	}

	// ------------------------------------------------------------------------
	async filterZens() {
		const tmpList: Models.ZenMasterCustomer[] = [];

		this.zensToShow = [];

		for (const item of this.zens) {
			let passedEnabledFilter = false;
			let passedTypeFilter = false;
			let passedAssessmentFilter = false;
			let passedRecentTrafficFilter = false;
			let pasedActiveBxFilter = false;
			let passedAccountOwnerFilters = false;
			let passedseFilters = false;
			let passedSpecialFilters = false;

			if (this.enabledFilter == null || this.enabledFilter === item.is_enabled)
				passedEnabledFilter = true;

			if (!this.zmTypeFilters || this.zmTypeFilters.length === 0
				|| (this.zmTypeFilters.length !== 0 && item.zcp_type && this.zmTypeFilters.includes(item.zcp_type)))
				passedTypeFilter = true;

			if (this.zmTypeFilters.length !== 0 && this.zmTypeFilters.includes('') && (!item.zcp_type || item.zcp_type === ''))
				passedTypeFilter = true;

			if (!this.assessmentFilters || this.assessmentFilters.length === 0 || this.assessmentFilters.includes(item.assessment))
				passedAssessmentFilter = true;

			const totalUsage: number = this.zenCustomersService.getUsageNoLoad(item, 'zenmaster')
				+ this.zenCustomersService.getUsageNoLoad(item, 'mediaconnect');

			if (!this.recentTrafficFilters || this.recentTrafficFilters.length === 0
				|| (this.recentTrafficFilters.includes(1) && totalUsage > 0) || (this.recentTrafficFilters.includes(0) && totalUsage === 0))
				passedRecentTrafficFilter = true;

			if (!this.activeBxFilters || this.activeBxFilters.length === 0
				|| (this.activeBxFilters.includes(1) && item.num_active_bx > 0)
				|| (this.activeBxFilters.includes(0) && item.num_active_bx === 0)
			)
				pasedActiveBxFilter = true;

			if (!this.accountOwnerFilters || this.accountOwnerFilters.length === 0 || this.accountOwnerFilters.includes(item['__accountOwner']))
				passedAccountOwnerFilters = true;

			if (!this.seFilters || this.seFilters.length === 0 || this.seFilters.includes(item['__se']))
				passedseFilters = true;

			if (!this.specialFilters || this.specialFilters.length === 0) {
				passedSpecialFilters = true;
			} else {
				passedSpecialFilters = true;

				if (this.specialFilters.includes('account_owner') && this.authUser && item['__accountOwner'] !== this.authUser.name)
					passedSpecialFilters = false;

				if (this.specialFilters.includes('tech_rep') && this.authUser && item['__se'] !== this.authUser.name)
					passedSpecialFilters = false;

				if (this.specialFilters.includes('not_active') && (item.is_enabled !== 1 || !AppConstants.unusedZENMasterAssessments.includes(item.assessment) || totalUsage !== 0))
					passedSpecialFilters = false;

				if (this.specialFilters.includes('no_notifications') && !item['__notifyWarning'])
					passedSpecialFilters = false;
			}

			let match: boolean = (passedEnabledFilter && passedTypeFilter && passedAssessmentFilter && passedRecentTrafficFilter
				&& pasedActiveBxFilter && passedAccountOwnerFilters && passedseFilters && passedSpecialFilters);


			if (match) this.zensToShow.push(item);
		} // for

		await MiscTools.delay(100);
		if (this.zensTable1)
			this.zensTable1.updateContent(this.zensToShow, 'WIPE-FILTER.cp-zem-masters-list', { showFavorite: true, showSalesEng: true, showSalesRep: true });

		this.loading = false;
	}
}

